import type { StoreApi } from "zustand/vanilla";
import type { SessionAssignationState } from "@context/sessionAssignation";
import type {
  AssignationSessionsPreviewInput,
  AssignationSessionsPreviewResult,
  SessionCrudInput,
  SessionCrudResult,
} from "@models/ISchema";
import type {
  SessionAssignationContext,
  SessionAssignationIssue,
} from "@services/types/session-assignation/sessionAssignationTypes";
import { unassignedInstructorsInput } from "@utils/unassignedInstructors";
import {
  buildAssignationPreviewInput,
  duplicateOccurrencesCount,
  SessionAssignationError,
} from "../adapters/occurrences";
import { reconcileAssignationPreview } from "../adapters/reconciliation";
import { rowsUnassignedInstructorIds } from "../adapters/unassignedInstructors";
import { freezeSnapshot } from "./immutable";

interface WorkflowDependencies {
  store: StoreApi<SessionAssignationState>;
  preview: (
    context: SessionAssignationContext,
    input: AssignationSessionsPreviewInput,
  ) => Promise<AssignationSessionsPreviewResult>;
  crud: (context: SessionAssignationContext, input: SessionCrudInput) => Promise<SessionCrudResult>;
  /** Whether the scenario registers group drop dates, read on every validation. */
  reportsUnassignedInstructors?: () => boolean;
}

interface CommitOptions {
  beforeSave?: () => Promise<boolean>;
  afterSave?: () => Promise<void>;
}

const issueForError = (error: unknown, fallback: string): SessionAssignationIssue => ({
  code: error instanceof SessionAssignationError ? error.code : fallback,
});

export const createSessionAssignationWorkflow = ({
  store,
  preview,
  crud,
  reportsUnassignedInstructors = () => false,
}: WorkflowDependencies) => {
  const validate = async ({ acknowledgeConsolidation = false } = {}) => {
    const current = store.getState();
    if (current.phase !== "editing") return "ignored" as const;
    if (duplicateOccurrencesCount(current.rows) && !acknowledgeConsolidation)
      return "consolidation-warning" as const;
    const token = current.beginValidation();
    if (!token) return "ignored" as const;
    const snapshot = freezeSnapshot({
      context: current.context,
      sections: current.sections,
      weeks: current.weeks,
      blocks: current.blocks,
      originals: current.originals,
      originalRows: current.originalRows,
      rows: current.rows,
      policy: current.policy,
    });
    try {
      const input = buildAssignationPreviewInput(
        snapshot.context.linkId,
        snapshot.sections.map(section => section.id),
        snapshot.rows,
      );
      const result = await preview(snapshot.context, input);
      if (!store.getState().isCurrent(token)) return "stale" as const;
      if (!result) throw new SessionAssignationError("PREVIEW_FAILED");
      if (result.validationErrors.length) {
        store.getState().failValidation(
          token,
          result.validationErrors.map(error => ({
            code: error.code,
            message: error.message ?? undefined,
          })),
        );
        return "invalid" as const;
      }
      const changes = reconcileAssignationPreview(result, snapshot);
      if (!changes.operations.length) {
        store.getState().failValidation(token, [{ code: "NO_PERSISTED_CHANGES" }]);
        return "no-changes" as const;
      }
      // Stored with the validation, so the commit sends the same unassigned instructors.
      const crudInput = {
        ...changes.input,
        ...unassignedInstructorsInput(
          rowsUnassignedInstructorIds(snapshot),
          reportsUnassignedInstructors(),
        ),
      };
      const validation = await crud(snapshot.context, crudInput);
      if (!store.getState().isCurrent(token)) return "stale" as const;
      if (!validation?.payload) throw new SessionAssignationError("VALIDATION_FAILED");
      const accepted = store
        .getState()
        .acceptValidation({ ...changes, input: crudInput, token, result: validation });
      return accepted ? ("validated" as const) : ("stale" as const);
    } catch (error) {
      if (!store.getState().isCurrent(token)) return "stale" as const;
      store.getState().failValidation(token, [issueForError(error, "VALIDATION_FAILED")]);
      return "invalid" as const;
    }
  };

  const commit = async ({ beforeSave, afterSave }: CommitOptions = {}) => {
    const current = store.getState();
    const validated = current.beginCommit();
    if (!validated) return "ignored" as const;
    try {
      if (beforeSave && !(await beforeSave())) {
        store.getState().failCommit(validated.token, { code: "SAVE_PREPARATION_FAILED" });
        return "preparation-failed" as const;
      }
      if (!store.getState().isCurrent(validated.token)) return "stale" as const;
      const result = await crud(current.context, {
        ...validated.input,
        dryRun: false,
        skipValidations: validated.result.userCanSkipValidations === true,
      });
      if (!store.getState().isCurrent(validated.token)) return "stale" as const;
      if (result?.commited !== true) throw new SessionAssignationError("SAVE_NOT_COMMITTED");
      if (!store.getState().completeCommit(validated.token)) return "stale" as const;
    } catch (error) {
      if (!store.getState().isCurrent(validated.token)) return "stale" as const;
      store.getState().failCommit(validated.token, issueForError(error, "SAVE_FAILED"));
      return "failed" as const;
    }
    // Commit already succeeded. A refresh failure must never allow the same mutation to be submitted again.
    try {
      await afterSave?.();
    } catch {
      if (store.getState().isCurrent(validated.token))
        store.setState({ issues: [{ code: "SAVED_REFRESH_FAILED" }] });
      return "saved-refresh-failed" as const;
    }
    return "saved" as const;
  };

  return { validate, commit };
};

export type SessionAssignationWorkflow = ReturnType<typeof createSessionAssignationWorkflow>;
